import { useCallback, useMemo, useRef, useState } from 'react';
import {
  dropUnmappedEdits,
  viewSections,
  type SectionDocument,
  type SectionEditModel,
  type SectionOpResult,
  type SectionUnmappedEdit,
  type SectionsView,
} from '@nopain-newsletter/core/sections';

/** Undo steps kept per session. Models are small, so this is a memory guard only. */
const MAX_HISTORY = 200;
/** Edits of the same field closer together than this are one undo step. */
const COALESCE_MS = 1200;

export type SectionOperation = (doc: SectionDocument, model: SectionEditModel) => SectionOpResult;

export type SaveState =
  | { status: 'clean' }
  | { status: 'dirty' }
  | { status: 'saving' }
  | { status: 'saved'; at: number }
  | { status: 'error'; message: string; errors: string[]; stale: boolean };

export interface SectionSaveResult {
  unmapped: SectionUnmappedEdit[];
}

/** What a failed save says. `stale`: the original changed on the server, so these edits cannot be saved at all. */
export interface SectionSaveFailure {
  message: string;
  errors?: string[];
  stale?: boolean;
}

export interface SectionEditor {
  doc: SectionDocument;
  view: SectionsView;
  model: SectionEditModel;
  dirty: boolean;
  canUndo: boolean;
  canRedo: boolean;
  save: SaveState;
  /** What the server could not apply at the last save. */
  unmapped: SectionUnmappedEdit[];
  /**
   * Runs one core operation on the current model. A refusal changes
   * nothing and is returned as it is, so the caller can show the core's
   * message. `coalesce`: edits with the same key shortly after each other
   * share one undo step (typing in one field).
   */
  apply: (operation: SectionOperation, coalesce?: string) => SectionOpResult;
  /** What the core would answer, without changing anything (for showing why something is not allowed). */
  tryOp: (operation: SectionOperation) => SectionOpResult;
  /** Goes up when the model changed by something else than an edit (undo, redo, a save that dropped edits), so open fields read their value again. */
  revision: number;
  undo: () => void;
  redo: () => void;
  saveNow: () => Promise<boolean>;
}

function sameModel(a: SectionEditModel, b: SectionEditModel): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

/**
 * State of one section editor session: the edit model, undo and redo (in
 * memory, gone on reload) and the save state. The original HTML never
 * changes here, and the model only changes through core operations.
 *
 * Saving is explicit, not automatic: a saved template is what automations
 * send from that moment, so a half-typed sentence must not go out by itself.
 */
export function useSectionEditor({
  doc,
  initialModel,
  onSave,
  now = Date.now,
}: {
  /** The analysed original. It must not change while this editor is mounted. */
  doc: SectionDocument;
  initialModel: SectionEditModel;
  /** Sends the model to the server. Rejects with a {@link SectionSaveFailure}. */
  onSave: (model: SectionEditModel, originalHash: string) => Promise<SectionSaveResult>;
  now?: () => number;
}): SectionEditor {
  const [model, setModel] = useState(initialModel);
  const [saved, setSaved] = useState(initialModel);
  const [past, setPast] = useState<SectionEditModel[]>([]);
  const [future, setFuture] = useState<SectionEditModel[]>([]);
  const [save, setSave] = useState<SaveState>({ status: 'clean' });
  const [unmapped, setUnmapped] = useState<SectionUnmappedEdit[]>([]);
  const [revision, setRevision] = useState(0);
  const lastEdit = useRef<{ key: string; at: number } | null>(null);
  const modelRef = useRef(model);
  modelRef.current = model;
  const saving = useRef(false);

  const view = useMemo(() => viewSections(doc, model), [doc, model]);
  const dirty = !sameModel(model, saved);

  const settle = useCallback(
    (next: SectionEditModel, savedModel: SectionEditModel) => {
      setSave((current) => {
        if (current.status === 'saving') return current;
        if (current.status === 'error' && current.stale) return current;
        if (!sameModel(next, savedModel)) return { status: 'dirty' };
        return current.status === 'saved' ? current : { status: 'clean' };
      });
    },
    [setSave],
  );

  const apply = useCallback(
    (operation: SectionOperation, coalesce?: string): SectionOpResult => {
      const current = modelRef.current;
      const result = operation(doc, current);
      if (!result.ok || sameModel(result.model, current)) return result;
      const at = now();
      const joined =
        coalesce !== undefined &&
        lastEdit.current?.key === coalesce &&
        at - lastEdit.current.at < COALESCE_MS;
      lastEdit.current = coalesce === undefined ? null : { key: coalesce, at };
      if (!joined) setPast((list) => [...list.slice(-(MAX_HISTORY - 1)), current]);
      setFuture([]);
      modelRef.current = result.model;
      setModel(result.model);
      settle(result.model, saved);
      return result;
    },
    [doc, now, saved, settle],
  );

  const tryOp = useCallback(
    (operation: SectionOperation): SectionOpResult => operation(doc, modelRef.current),
    [doc],
  );

  const undo = useCallback(() => {
    const previous = past[past.length - 1];
    if (previous === undefined) return;
    lastEdit.current = null;
    // Read now: the updater below runs after the ref has moved on.
    const current = modelRef.current;
    setPast(past.slice(0, -1));
    setFuture((list) => [current, ...list]);
    modelRef.current = previous;
    setModel(previous);
    setRevision((n) => n + 1);
    settle(previous, saved);
  }, [past, saved, settle]);

  const redo = useCallback(() => {
    const next = future[0];
    if (next === undefined) return;
    lastEdit.current = null;
    const current = modelRef.current;
    setFuture(future.slice(1));
    setPast((list) => [...list, current]);
    modelRef.current = next;
    setModel(next);
    setRevision((n) => n + 1);
    settle(next, saved);
  }, [future, saved, settle]);

  const saveNow = useCallback(async (): Promise<boolean> => {
    if (saving.current) return false;
    const sent = modelRef.current;
    saving.current = true;
    lastEdit.current = null;
    setSave({ status: 'saving' });
    try {
      const result = await onSave(sent, doc.sourceHash);
      // What the server stored: the model without the edits it could not apply.
      const stored = dropUnmappedEdits(sent, result.unmapped);
      setUnmapped(result.unmapped);
      setSaved(stored);
      if (sameModel(modelRef.current, sent)) {
        // Nothing was typed while the save ran: show exactly what is stored.
        if (!sameModel(stored, sent)) {
          modelRef.current = stored;
          setModel(stored);
          setPast([]);
          setFuture([]);
          setRevision((n) => n + 1);
        }
        setSave({ status: 'saved', at: now() });
      } else {
        setSave({ status: 'dirty' });
      }
      return true;
    } catch (err) {
      const failure = (err ?? {}) as SectionSaveFailure;
      setSave({
        status: 'error',
        message:
          typeof failure.message === 'string' && failure.message !== ''
            ? failure.message
            : 'The section edits could not be saved. Try again shortly.',
        errors: Array.isArray(failure.errors) ? failure.errors : [],
        stale: failure.stale === true,
      });
      return false;
    } finally {
      saving.current = false;
    }
  }, [doc, now, onSave]);

  return {
    doc,
    view,
    model,
    dirty,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    save,
    unmapped,
    apply,
    tryOp,
    revision,
    undo,
    redo,
    saveNow,
  };
}
